import type { AnyAction } from 'redux';

import * as actions from 'app/redux/actions/hotelAdmin';
import type { ExtranetBookingWithNbNight } from 'app/redux/models/ExtranetBooking/ExtranetBooking';
import type { HotelAdminContact } from 'app/redux/models/Hotel/Hotel';
import type { IHotel } from 'app/typings';
import type { Booking } from 'app/typings/adminBookings/bookings';

export type HotelAdmin = {
  hotelId: number | undefined;
  hotels: IHotel[];
  isLoading: boolean;
  isEditing: boolean;
  isValidationFailed: boolean;
  isViewValidationFailed: boolean;
  bookings: ExtranetBookingWithNbNight[];
  booking: Booking | undefined;
  bookingsCSV: { isLoading: boolean; data: unknown };
  feedbacks: unknown[];
  hotelMarks: unknown;
  contacts: {
    isLoading: boolean;
    data: HotelAdminContact | undefined;
  };
  billsPage: {
    billPdfObject: {
      pdf: Blob;
      filename: string;
    } | null;
    isDownloading: boolean;
  };
};

export const initialState: HotelAdmin = {
  hotelId: undefined,
  hotels: [],
  isLoading: false,
  isEditing: false,
  isValidationFailed: false,
  isViewValidationFailed: false,
  bookings: [],
  booking: undefined,
  bookingsCSV: { isLoading: false, data: undefined },
  feedbacks: [],
  hotelMarks: undefined,
  billsPage: {
    billPdfObject: null,
    isDownloading: false,
  },
  contacts: {
    isLoading: false,
    data: undefined,
  },
};

export function hotelAdmin(
  state = initialState,
  action: AnyAction
): HotelAdmin {
  if (actions.updateHotelId.match(action)) {
    return {
      ...state,
      hotelId: action.payload.hotelId,
      isEditing: false,
      isValidationFailed: false,
      isViewValidationFailed: false,
    };
  }

  if (actions.startLoading.match(action)) {
    return {
      ...state,
      isLoading: true,
    };
  }

  if (actions.stopLoading.match(action)) {
    return {
      ...state,
      isLoading: false,
    };
  }

  if (actions.fetchBookingsCSVSuccess.match(action)) {
    return {
      ...state,
      bookingsCSV: { isLoading: false, data: action.payload.result },
    };
  }

  if (actions.fetchHotelsSuccess.match(action)) {
    return {
      ...state,
      hotels: action.payload.result,
    };
  }

  if (actions.downloadBill.match(action)) {
    return {
      ...state,
      billsPage: {
        ...state.billsPage,
        isDownloading: true,
      },
    };
  }

  if (actions.downloadBillSuccess.match(action)) {
    const { pdf, filename } = action.payload;

    return {
      ...state,
      billsPage: {
        ...state.billsPage,
        billPdfObject: {
          pdf,
          filename,
        },
      },
    };
  }

  if (actions.downloadBillFailure.match(action)) {
    return {
      ...state,
      billsPage: {
        ...state.billsPage,
        isDownloading: false,
      },
    };
  }

  if (actions.resetBillObjectPdf.match(action)) {
    return {
      ...state,
      billsPage: {
        ...state.billsPage,
        isDownloading: false,
        billPdfObject: initialState.billsPage.billPdfObject,
      },
    };
  }

  if (actions.fetchContacts.match(action)) {
    return {
      ...state,
      contacts: {
        isLoading: true,
        data: undefined,
      },
    };
  }

  if (
    actions.fetchContactsSuccess.match(action) ||
    actions.saveContactsSuccess.match(action)
  ) {
    return {
      ...state,
      contacts: {
        isLoading: false,
        data: { ...action.payload.res },
      },
    };
  }

  return state;
}
